import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        const userInfo = localStorage.getItem('userInfo');

        if (token && userInfo) {
          // Verify token is still valid by getting user info
          try {
            const response = await api.get('/admin/me');
            setUser(response.data);
            setIsAuthenticated(true);
          } catch (error) {
            // Token is invalid, try to refresh
            if (refreshToken) {
              try {
                await refreshAccessToken();
              } catch (refreshError) {
                // Both tokens are invalid, clear everything
                clearAuth();
              }
            } else {
              clearAuth();
            }
          }
        } else {
          clearAuth();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Clear authentication data
  const clearAuth = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/admin/refresh', {
        refresh_token: refreshToken
      });

      const { access_token } = response.data;
      localStorage.setItem('accessToken', access_token);
      
      // Update axios default header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuth();
      throw error;
    }
  }, [clearAuth]);

  // Login function
  const login = useCallback(async (username, password) => {
    try {
      setLoading(true);
      const response = await api.post('/admin/login', { username, password });
      
      const { access_token, refresh_token, role, username: userUsername, full_name } = response.data;
      
      // Store tokens and user info
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('userInfo', JSON.stringify({
        username: userUsername,
        role,
        full_name
      }));
      
      // Set axios default header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Update state
      setUser({
        id: response.data.id,
        username: userUsername,
        role,
        full_name,
        auth_type: response.data.auth_type
      });
      setIsAuthenticated(true);
      
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    clearAuth();
    // Remove axios default header
    delete api.defaults.headers.common['Authorization'];
  }, [clearAuth]);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return user && user.role === role;
  }, [user]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    return user && roles.includes(user.role);
  }, [user]);

  // Get user info
  const getUserInfo = useCallback(() => {
    return user;
  }, [user]);

  // Update user info
  const updateUserInfo = useCallback((newUserInfo) => {
    setUser(prev => ({ ...prev, ...newUserInfo }));
    localStorage.setItem('userInfo', JSON.stringify({ ...user, ...newUserInfo }));
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    hasRole,
    hasAnyRole,
    getUserInfo,
    updateUserInfo,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
